import { User } from "@dreamhub/designsystem/icons";
import UserAvatar from "@/components/controls/UserAvatar";
import Badge from "@/components/ui/badge";
import IconContainer from "@/components/ui/icon-container";
import { TextVariant } from "@/components/ui/text";
import { getFullName } from "@/entities/people/helpers";
import { usePeople } from "@/entities/people/hooks";
import { useUsers } from "@/entities/users/hooks";
import { cn } from "@/utils/utils";
import HighlightedText from "@/components/controls/HighlightedText";
import { useSearchHighlight } from "@/contexts/SearchHighlightContext";

const userAndPersonPrefixes = ["u-", "p-"];

interface Props {
  id: string;
  className?: string;
  textVariant?: TextVariant;
}

const UserOrPersonBadge: React.FC<Props> = ({ id, className, textVariant }) => {
  const { searchQuery, isSearchActive } = useSearchHighlight();
  const isNotAnId = !userAndPersonPrefixes.some((prefix) =>
    id?.startsWith(prefix)
  );
  const isPerson = id?.startsWith("p-");
  const {
    entities: [personData]
  } = usePeople([id], { enabled: isPerson });
  const userData = useUsers([id], { enabled: !isPerson }).users[0];

  const getBadgeText = () => {
    if (isNotAnId) {
      return id;
    }
    if (isPerson && personData) {
      return getFullName(personData);
    }
    return userData?.displayName;
  };

  const getBadgeIcon = () => {
    if (isNotAnId || isPerson || !userData) {
      return (
        <IconContainer size="sm">
          <User />
        </IconContainer>
      );
    }
    return (
      <UserAvatar
        userData={userData}
        size="S"
      />
    );
  };

  const badgeText = getBadgeText();

  return (
    <Badge
      className={cn("px-[6px] py-1 border-none", className)}
      textVariant={textVariant || "smBody"}
      text={isSearchActive ? undefined : badgeText}
      icon={getBadgeIcon()}
    >
      {isSearchActive && badgeText ? (
        <HighlightedText text={badgeText} query={searchQuery} />
      ) : null}
    </Badge>
  );
};

export default UserOrPersonBadge;
